import { infiniteQueryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, OrderDirection, OrderInput } from '#features/api/types';

const blogPostsDoc = graphql(`
  query BlogPosts(
    $offset: Int
    $limit: Int
    $orders: [OrderInput]
    $filters: [FilterInput]
    $tag: String
  ) {
    blogs {
      list(offset: $offset, limit: $limit, orders: $orders, filters: $filters, tag: $tag) {
        blogs {
          id
          readingTime
          createdAt
          ogDescription
          title
          date
          previewImage
          slug
          tags
        }
        hasMore
      }
    }
  }
`);

export const getBlogPosts = async (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
  tag?: string;
}) => {
  const data = await gqlFetcher(blogPostsDoc, {
    offset: input.offset,
    limit: input.limit,
    orders: input.orders,
    filters: input.filters,
    tag: input.tag,
  });

  return { records: data.blogs?.list?.blogs ?? [], hasMore: data.blogs?.list?.hasMore };
};

export const blogPostsQueryOptions = (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
  tag?: string;
}) => {
  return {
    queryKey: ['blogPosts', input.offset, input.limit, input.orders, input.filters, input.tag],
    queryFn: () => getBlogPosts(input),
  };
};

/** Pages contain only card fields; article bodies are fetched when an article opens. */
const BLOG_PAGE_SIZE = 100;

/**
 * The blog list, shared by the /blog route loader (SSR) and the list hook.
 *
 * Both must use this one definition: the query key is what ties the page fetched on the
 * server to the cache the component reads on the client. When they drifted apart, /blog
 * shipped HTML with a heading and no articles, and the list only appeared after hydration.
 */
export const blogPostsInfiniteQueryOptions = (tag?: string) =>
  infiniteQueryOptions({
    queryKey: tag ? ['blogPosts', 'tag', tag.trim().toLowerCase()] : ['blogPosts'],
    queryFn: ({ pageParam }) =>
      getBlogPosts({
        offset: pageParam * BLOG_PAGE_SIZE,
        limit: BLOG_PAGE_SIZE,
        tag,
        orders: [{ direction: OrderDirection.DESC, field: 'date' }],
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage?.hasMore ? allPages.length : undefined),
  });

const blogTagsDoc = graphql(`
  query BlogTags {
    blogs {
      tags
    }
  }
`);

export const blogTagsQueryOptions = () => ({
  queryKey: ['blogPosts', 'allTags'],
  queryFn: async () => (await gqlFetcher(blogTagsDoc)).blogs?.tags ?? [],
});

const blogPostBySlugDoc = graphql(`
  query BlogPost($slug: String!) {
    blogs {
      findBySlug(slug: $slug) {
        content
        readingTime
        date
        updatedAt
        id
        keywords
        ogDescription
        ogTitle
        previewImage
        tags
        title
      }
    }
  }
`);

export const getBlogPostBySlug = async (slug: string) => {
  const data = await gqlFetcher(blogPostBySlugDoc, { slug });
  return data.blogs?.findBySlug ?? null;
};

export const blogPostBySlugQueryOptions = (slug: string) => {
  return {
    queryKey: ['blogPost', slug],
    queryFn: () => getBlogPostBySlug(slug),
  };
};

export type BlogPostData = NonNullable<Awaited<ReturnType<typeof getBlogPostBySlug>>>;

/**
 * Unpublished article for the admin's preview. The token is the whole credential: the admin
 * panel signs it for one article and twenty minutes, and the gateway answers the same
 * "not found" for anything else. Same fields as the public query, so the page is the same page.
 */
const blogPreviewDoc = graphql(`
  query BlogPreview($token: String!) {
    blogs {
      preview(token: $token) {
        content
        readingTime
        date
        updatedAt
        id
        keywords
        ogDescription
        ogTitle
        previewImage
        tags
        title
        slug
      }
    }
  }
`);

export type BlogPreviewData = BlogPostData & { slug: string };

export const getBlogPreview = async (token: string): Promise<BlogPreviewData | null> => {
  try {
    const data = await gqlFetcher(blogPreviewDoc, { token });
    return data.blogs?.preview ?? null;
  } catch {
    // Expired, forged or foreign token, or a deleted article: all one outcome for the page.
    return null;
  }
};

/**
 * Only `__typename` is selected: the caller ignores the result, so this keeps the selection
 * minimal. Note the union must not be narrowed with an inline fragment (`... on Operation`) —
 * the gateway's RequestContextInjector ClassCasts selections to Field (see postOgFragment).
 */
const incrementBlogViewDoc = graphql(`
  mutation IncrementBlogView($id: UUID!) {
    blog {
      incrementView(id: $id) {
        __typename
      }
    }
  }
`);

/**
 * Counts one view of a blog post. Fire-and-forget by contract: this never rejects and never
 * returns anything, so a failing counter can never break reading an article. A view is a
 * nice-to-have metric; the article is the product.
 */
export const incrementBlogView = async (id: string): Promise<void> => {
  try {
    await gqlFetcher(incrementBlogViewDoc, { id });
  } catch {
    // Swallowed on purpose — see above. Not reported to Sentry either: gqlFetcher already
    // reports genuine network faults, and a noisy counter would add nothing but alert fatigue.
  }
};
